import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type {
  KioskCartRecommendationCardsBlockConfig,
  KioskCartRecommendationCardsBlockScenarioContent,
} from '@front-platform/content-builder';
import {
  kioskCartRecommendationCardsBlockResolveConfig,
  recommendationItemsForDisplay,
} from '@front-platform/content-builder';
import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  effect,
  inject,
  input,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { catchError, distinctUntilChanged, filter, map, switchMap, take } from 'rxjs/operators';
import { merge, Observable, of } from 'rxjs';

import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { DoodProductModel } from '@core/models/product.model';
import { ModalsService } from '@core/services/modals/modals.service';
import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';
import { CB2_PREVIEW_BLOC_KEY } from '@core/services/cb2-preview/cb2-preview-bloc-key.token';
import { KIOSK_CART_RECOMMENDATION_CARDS_BLOCK_COMPONENT } from '@core/services/cb2-preview/cb2-preview-bloc-types';
import { isAnyKioskSuggestionsPreviewModalOpen } from '@core/services/cb2-preview/kiosk-suggestions-preview-modal.util';
import { previewScenarioData } from '@core/services/cb2-preview/preview-scenario-data.util';
import {
  previewScenarioForBloc$,
  resolvePreviewScenarioForBloc,
} from '@core/services/cb2-preview/preview-scenario-for-bloc.util';
import { RecommendationsApiService } from '@core/services/api/recommendations/recommendations-api.service';
import type {
  RecommendationItem,
  RecommendationsResponse,
} from '@core/services/api/recommendations/recommendations-api.service';
import { CartStoreSelector } from '@common/selectors/cart.selector';
import { OrdersService } from '@core/services/orders/orders.service';
import { CartKeys } from '@config/keys/cart.keys';
import { isSameCartContent } from '@shared/utils/cart/cart-equality.util';
import {
  KioskSuggestionsModalComponent,
  KioskSuggestionsModalConfig,
} from '../kiosk-suggestions-modal/kiosk-suggestions-modal.component';

export interface KioskCartRecommendationCardsBlockRuntimeConfig {
  'secondary_color'?: IContentBuilderFieldColor;
}

export type KioskCartRecommendationCardsBlockConfigInput = Partial<
  Omit<
    KioskCartRecommendationCardsBlockConfig,
    keyof KioskCartRecommendationCardsBlockRuntimeConfig
  > &
    KioskCartRecommendationCardsBlockRuntimeConfig
>;
export type KioskCartRecommendationCardsBlockResolvedConfig = Omit<
  KioskCartRecommendationCardsBlockConfig,
  keyof KioskCartRecommendationCardsBlockRuntimeConfig
> &
  Partial<KioskCartRecommendationCardsBlockRuntimeConfig>;
export interface KioskCartRecommendationCardsBlockContract extends ConfigComponentContract<
  KioskCartRecommendationCardsBlockConfigInput,
  KioskCartRecommendationCardsBlockResolvedConfig
> {}

@Component({
  selector: 'app-kiosk-cart-recommendation-cards-block',
  templateUrl: './kiosk-cart-recommendation-cards-block.component.html',
  standalone: false,
})
export class KioskCartRecommendationCardsBlockComponent
  implements OnInit, KioskCartRecommendationCardsBlockContract
{
  readonly config = input<KioskCartRecommendationCardsBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    KioskCartRecommendationCardsBlockConfig,
    KioskCartRecommendationCardsBlockRuntimeConfig
  >(kioskCartRecommendationCardsBlockResolveConfig, () => ({ config: this.config() }));
  readonly heading = computed(() => this.resolvedConfig()['heading'] as unknown as string);
  readonly buttonBackgroundColor = computed(
    () => this.resolvedConfig()['button_background_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly buttonTextColor = computed(
    () => this.resolvedConfig()['button_text_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly secondaryColor = computed(
    () => this.resolvedConfig()['secondary_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly buttonLabel = computed(() => this.resolvedConfig()['button_label'] as unknown as string);
  readonly textColor = computed(
    () => this.resolvedConfig()['text_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly backgroundColor = computed(
    () => this.resolvedConfig()['background_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly cardColor = computed(
    () => this.resolvedConfig()['card_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly minRecommendations = computed(
    () => this.resolvedConfig()['min_recommendations'] as unknown,
  );
  readonly maxRecommendations = computed(
    () => this.resolvedConfig()['max_recommendations'] as unknown,
  );

  private readonly modalsService: ModalsService<KioskSuggestionsModalConfig> = inject(
    ModalsService<KioskSuggestionsModalConfig>,
  );
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cb2PreviewState = inject(Cb2PreviewStateService);
  private readonly previewBlocKey = inject(CB2_PREVIEW_BLOC_KEY, { optional: true });
  private readonly previewModalInputs = computed(() => ({
    heading: this.heading() || 'kiosk-recommendation-cards-modal.heading',
    buttonBackgroundColor: this.buttonBackgroundColor(),
    buttonTextColor: this.buttonTextColor(),
    textColor: this.textColor(),
    secondaryColor: this.secondaryColor(),
    backgroundColor: this.backgroundColor(),
    cardColor: this.cardColor(),
    buttonLabel: this.buttonLabel(),
  }));
  private readonly recommendationsApiService = inject(RecommendationsApiService);
  private readonly cartSelector = inject(CartStoreSelector);
  private readonly ordersService = inject(OrdersService);
  private readonly previewScenario$ = previewScenarioForBloc$(
    KIOSK_CART_RECOMMENDATION_CARDS_BLOCK_COMPONENT,
  );

  // The kiosk cart page does not mark a shop as active. The cart already carries
  // the shop id, which is all the recommendations endpoint needs.
  private readonly recommendations$: Observable<DoodProductModel[]> =
    this.cartSelector.selectActive$.pipe(
      distinctUntilChanged(isSameCartContent),
      switchMap(cart => {
        const marketplaceId = cart?.[CartKeys.Marketplace];
        const shopId = cart?.[CartKeys.Shop];
        const order = cart ? this.ordersService.mapCartToOrderValues(cart, []) : null;
        if (!order || !marketplaceId || typeof shopId !== 'string' || shopId.length === 0) {
          return of([]);
        }
        return this.recommendationsApiService
          .getRecommendations$({
            order,
            marketplace: marketplaceId,
            shop: shopId,
          })
          .pipe(
            map((res: RecommendationsResponse) => res?.recommendations ?? []),
            map((items: RecommendationItem[]) =>
              items
                .filter((item): item is RecommendationItem => item?.type === 'product')
                .map(item => item.data as DoodProductModel)
                .filter((product): product is DoodProductModel => !!product?.id),
            ),
            catchError(() => of([])),
          );
      }),
    );

  constructor() {
    effect(() => {
      if (!this.cb2PreviewState.isActive()) {
        return;
      }
      const inputs = this.previewModalInputs();
      const content = previewScenarioData<KioskCartRecommendationCardsBlockScenarioContent>(
        resolvePreviewScenarioForBloc(
          this.cb2PreviewState.previewScenarios(),
          this.previewBlocKey,
          KIOSK_CART_RECOMMENDATION_CARDS_BLOCK_COMPONENT,
        ),
        KIOSK_CART_RECOMMENDATION_CARDS_BLOCK_COMPONENT,
      );
      if (!content?.isOpen) {
        return;
      }
      untracked(() =>
        this.publishPreviewModal(inputs, content.suggestions as unknown as DoodProductModel[]),
      );
    });

    merge(
      this.router.events.pipe(
        filter((e): e is NavigationStart => e instanceof NavigationStart),
        map(() => 'navigation' as const),
      ),
    )
      .pipe(takeUntilDestroyed())
      .subscribe(source => {
        if (source === 'navigation') {
          this.modalsService.close(KioskSuggestionsModalComponent.handle);
        }
      });
  }

  ngOnInit() {
    if (this.cb2PreviewState.isActive()) {
      this.previewScenario$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(scenario => {
        const content = previewScenarioData<KioskCartRecommendationCardsBlockScenarioContent>(
          scenario,
          KIOSK_CART_RECOMMENDATION_CARDS_BLOCK_COMPONENT,
        );
        if (content?.isOpen) {
          this.publishPreviewModal(
            this.previewModalInputs(),
            content.suggestions as unknown as DoodProductModel[],
          );
          return;
        }
        this.closeSharedPreviewModalIfIdle();
      });
      return;
    }

    this.recommendations$
      .pipe(
        map(products => this.productsForDisplay(products)),
        filter(products => products.length > 0),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(products => {
        this.openModal(products);
      });
  }

  private productsForDisplay(products: readonly DoodProductModel[]): DoodProductModel[] {
    return [
      ...recommendationItemsForDisplay(
        products,
        this.minRecommendations(),
        this.maxRecommendations(),
      ),
    ];
  }

  private closeSharedPreviewModalIfIdle(): void {
    if (isAnyKioskSuggestionsPreviewModalOpen(this.cb2PreviewState.previewScenarios())) {
      return;
    }
    this.modalsService.close(KioskSuggestionsModalComponent.handle);
  }

  private publishPreviewModal(
    inputs: {
      heading: string;
      buttonBackgroundColor: IContentBuilderFieldColor;
      buttonTextColor: IContentBuilderFieldColor;
      textColor: IContentBuilderFieldColor;
      secondaryColor: IContentBuilderFieldColor;
      backgroundColor: IContentBuilderFieldColor;
      cardColor: IContentBuilderFieldColor;
      buttonLabel: string;
    },
    products: readonly DoodProductModel[],
  ): void {
    const displayed = this.productsForDisplay(products);
    if (displayed.length === 0) {
      this.modalsService.close(KioskSuggestionsModalComponent.handle);
      return;
    }
    const data = { ...inputs, products: displayed };
    this.modalsService.open(KioskSuggestionsModalComponent.handle, undefined, data);
    this.modalsService.setData(KioskSuggestionsModalComponent.handle, data);
  }

  private openModal(products: DoodProductModel[]): void {
    this.modalsService.close(KioskSuggestionsModalComponent.handle);
    this.modalsService.open(KioskSuggestionsModalComponent.handle, undefined, {
      ...this.previewModalInputs(),
      products,
    });
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(KioskCartRecommendationCardsBlockComponent, {
  'keyAliases': {},
});
